import React, {
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect
} from 'react';
import { Value as PhoneNumberValue } from 'react-phone-number-input';
import { Handle, NodeProps, Position } from 'reactflow';
import CustomTooltip from '../../../../components/CustomTooltip/CustomTooltip';
import BotBuilderNode from '../BotBuilderNode/BotBuilderNode';
import SendSmsTreeNode from '../../../../types/BotBuilder/SendSmsTreeNode';
import BotResponseTreeNode from '../../../../types/BotBuilder/BotResponseTreeNode';
import BotBuilderContext from '../../../../context/BotBuilderContext/BotBuilderContext';
import PhoneInput from '../../../../components/PhoneInput/PhoneInput';
import { Label } from '../../../../components/ui/label';
import useDebouncedEffect from '../../../../hooks/useDebouncedEffect';
import RichTextEditor from '../../../../components/RichTextEditor/RichTextEditor';
import PreviewModeContext from '../../../../context/PreviewModeContext/PreviewModeContext';
import SlotDisplayConditionList from '../SlotDisplayConditionList/SlotDisplayConditionList';
import SlotDisplayCondition from '../../../../types/BotBuilder/SlotDisplayCondition';

export type SendSmsNodeData = {
  node: BotResponseTreeNode;
  isLocked?: boolean;
};

const isSendSmsTreeNode = (content: any): content is SendSmsTreeNode => {
  return content instanceof SendSmsTreeNode;
};

function SendSmsNode(props: NodeProps<SendSmsNodeData>): React.ReactElement {
  const { data, isConnectable } = props;
  const { onNodeUpdate, onNodeDelete } = useContext(BotBuilderContext);
  const { isPreviewMode, setIsPreviewMode } = useContext(PreviewModeContext);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  const sendSmsNode = isSendSmsTreeNode(data.node.content)
    ? data.node.content
    : null;

  const [phoneNumber, setPhoneNumber] = useState<PhoneNumberValue>(
    sendSmsNode?.phoneNumber as PhoneNumberValue
  );
  const [content, setContent] = useState<string | null>(
    sendSmsNode?.content ?? null
  );
  const [slotDisplayConditions, setSlotDisplayConditions] = useState<
    SlotDisplayCondition[]
  >(sendSmsNode?.slotDisplayConditions ?? []);
  const [isEditingContent, setIsEditingContent] = useState<boolean>(false);

  const updateNodeData = useCallback(
    (updates: Partial<SendSmsTreeNode>) => {
      if (!sendSmsNode) return;

      let hasChanges = false;
      Object.entries(updates).forEach(([key, value]) => {
        if (sendSmsNode[key as keyof SendSmsTreeNode] !== value) {
          hasChanges = true;
        }
      });

      if (hasChanges) {
        Object.assign(sendSmsNode, updates);
        onNodeUpdate(data.node);
      }
    },
    [sendSmsNode, onNodeUpdate, data.node]
  );

  const handleContentEditEnd = useCallback(() => {
    setIsEditingContent(false);
  }, []);

  const handleEditorPreview = useCallback(() => {
    handleContentEditEnd();
  }, [handleContentEditEnd]);

  useEffect(() => {
    if (sendSmsNode) {
      setPhoneNumber(sendSmsNode.phoneNumber as PhoneNumberValue);
      setContent(sendSmsNode.content);
    }
  }, [sendSmsNode]);

  useEffect(() => {
    if (isPreviewMode && isEditingContent) {
      handleContentEditEnd();
    }
  }, [isPreviewMode, isEditingContent, handleContentEditEnd]);

  useDebouncedEffect(
    () => {
      if (sendSmsNode) {
        const phoneNumberString = phoneNumber || '';
        if (phoneNumberString !== sendSmsNode.phoneNumber) {
          updateNodeData({ phoneNumber: phoneNumberString });
        }
      }
    },
    [phoneNumber, sendSmsNode, updateNodeData],
    300
  );

  useDebouncedEffect(
    () => {
      if (sendSmsNode && content !== sendSmsNode.content) {
        updateNodeData({ content: content || '' });
      }
    },
    [content, sendSmsNode, updateNodeData],
    300
  );

  const handleSlotDisplayConditionsChange = useCallback(
    (conditions: SlotDisplayCondition[]) => {
      setSlotDisplayConditions(conditions);
      updateNodeData({ slotDisplayConditions: conditions });
    },
    [updateNodeData]
  );

  const handlePhoneNumberChange = useCallback((value: PhoneNumberValue) => {
    setPhoneNumber(value);
  }, []);

  const handleContentEditStart = useCallback(
    (e: React.MouseEvent) => {
      if (data.isLocked) return;
      e.stopPropagation();
      setIsEditingContent(true);
      setIsPreviewMode(false);
    },
    [setIsPreviewMode, data.isLocked]
  );

  const handleContentChange = useCallback((updatedContent: string) => {
    setContent(updatedContent || null);
  }, []);

  const handleNodeClick = useCallback(
    (e: React.MouseEvent) => {
      if (isEditingContent) {
        const target = e.target as HTMLElement;
        const richTextEditor = target.closest('.rich-text-editor-wrapper');

        if (!richTextEditor) {
          handleContentEditEnd();
        }
      }
    },
    [isEditingContent, handleContentEditEnd]
  );

  return (
    <BotBuilderNode
      title='Send SMS'
      sourceNode={data.node}
      onNodeDelete={async () => {
        onNodeDelete(data.node);
      }}
      deleteButtonRef={deleteButtonRef}
      showAddButton={false}
      isLocked={data.isLocked}
    >
      <Handle
        type='target'
        position={Position.Top}
        style={{ top: -8 }}
        isConnectable={isConnectable}
        id='c'
      />

      <div
        role='button'
        tabIndex={0}
        aria-label='SMS node configuration'
        className='flex flex-col gap-2 w-full bg-transparent p-0 text-left'
        onClick={handleNodeClick}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && isEditingContent) {
            handleContentEditEnd();
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleNodeClick(e as any);
          }
        }}
      >
        <div className='flex flex-col gap-2'>
          <Label className='text-sm font-medium'>Phone number</Label>
          <PhoneInput
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            placeholder='Enter phone number'
            defaultCountry='GB'
            className='bg-white'
            disabled={data.isLocked}
          />
        </div>

        <div className='flex flex-col gap-2'>
          <Label className='text-sm font-medium'>SMS content</Label>
          {!isPreviewMode && isEditingContent ? (
            <section aria-label='SMS content editor'>
              <RichTextEditor
                textContent={content}
                onChange={handleContentChange}
                onPreview={handleEditorPreview}
                isEditable
                showToolbar
                placeholder='Enter SMS message'
                outputFormat='plaintext'
              />
            </section>
          ) : (
            <CustomTooltip content='Edit SMS content'>
              <button
                type='button'
                onClick={handleContentEditStart}
                className='cursor-pointer w-full border-none p-0 text-left bg-white text-sm rounded-lg'
              >
                {!content || content.trim() === '' ? (
                  <div className='px-3 py-2 text-sm text-muted-foreground'>
                    Enter SMS message
                  </div>
                ) : (
                  <RichTextEditor
                    textContent={content}
                    isEditable={false}
                    showToolbar={false}
                    className='preview-mode text-sm'
                    outputFormat='plaintext'
                  />
                )}
              </button>
            </CustomTooltip>
          )}
        </div>

        <SlotDisplayConditionList
          content={content ?? ''}
          slotDisplayConditions={slotDisplayConditions}
          onChange={handleSlotDisplayConditionsChange}
          disabled={data.isLocked}
        />
      </div>

      <Handle
        type='source'
        position={Position.Bottom}
        style={{ bottom: -8 }}
        isConnectable={isConnectable}
        id='s'
      />
    </BotBuilderNode>
  );
}

export default SendSmsNode;
